import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Play, Trash2, Save, RefreshCw, Clock, CheckCircle, XCircle, AlertCircle, Edit3, StopCircle, RotateCcw, Copy, ExternalLink } from 'lucide-react';
import { useSofteamboardStore } from '../store/softeamboardStore';
import AppHeader from '../components/common/AppHeader';
import { useWebSocket } from '../hooks/useWebSocket';
import { ConfirmDialog } from '../components/shared/ConfirmDialog';
import Toast from '../components/ui/Toast';

const WorkflowRequestDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const {
    selectedWorkflowRequest,
    workflowLoading,
    workflowError,
    needs,
    getCandidatesForNeed,
    loadCandidatesForNeed,
    getWorkflowRequest,
    triggerWorkflowRequest,
    updateWorkflowRequest,
    deleteWorkflowRequest,
    cancelWorkflowRequest,
    retryWorkflowRequest,
    setSelectedWorkflowRequest
  } = useSofteamboardStore();

  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({
    status: '',
    lastError: ''
  });
  const [localLoading, setLocalLoading] = useState({});
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(Date.now());
  const [confirmAction, setConfirmAction] = useState({ open: false, type: null });
  const [toast, setToast] = useState(null);

  const { isConnected } = useWebSocket();

  useEffect(() => {
    if (id) {
      getWorkflowRequest(id);
      setLastRefresh(Date.now());
    }
    
    return () => {
      setSelectedWorkflowRequest(null);
    };
  }, [id]);

  useEffect(() => {
    if (selectedWorkflowRequest && !isEditing) {
      setEditForm({
        status: selectedWorkflowRequest.status || '',
        lastError: selectedWorkflowRequest.lastError || ''
      });
    }
  }, [selectedWorkflowRequest, isEditing]);

  useEffect(() => {
    if (!autoRefresh || !selectedWorkflowRequest) return;
    if (!['PENDING', 'IN_PROGRESS'].includes(selectedWorkflowRequest.status)) return;

    const interval = setInterval(() => {
      getWorkflowRequest(id);
      setLastRefresh(Date.now());
    }, 15000);

    return () => clearInterval(interval);
  }, [autoRefresh, selectedWorkflowRequest?.status, id]);

  const handleRefresh = () => {
    getWorkflowRequest(id);
    setLastRefresh(Date.now());
  };

  const handleTrigger = async () => {
    setLocalLoading(prev => ({ ...prev, trigger: true }));
    try {
      await triggerWorkflowRequest(id);
      setToast({ message: 'Workflow triggered successfully', type: 'success' });
      setTimeout(() => getWorkflowRequest(id), 1000);
    } catch (error) {
      setToast({ message: 'Failed to trigger workflow', type: 'error' });
    } finally {
      setLocalLoading(prev => ({ ...prev, trigger: false }));
    }
  };

  const handleCancel = () => {
    setConfirmAction({ open: true, type: 'cancel' });
  };

  const executeCancel = async () => {
    setLocalLoading(prev => ({ ...prev, cancel: true }));
    try {
      await cancelWorkflowRequest(id);
      setToast({ message: 'Workflow cancelled', type: 'success' });
      setTimeout(() => getWorkflowRequest(id), 1000);
    } catch (error) {
      setToast({ message: 'Failed to cancel workflow', type: 'error' });
    } finally {
      setLocalLoading(prev => ({ ...prev, cancel: false }));
      setConfirmAction({ open: false, type: null });
    }
  };

  const handleRetry = async () => {
    setLocalLoading(prev => ({ ...prev, retry: true }));
    try {
      await retryWorkflowRequest(id);
      setToast({ message: 'Workflow retry initiated', type: 'success' });
      setTimeout(() => getWorkflowRequest(id), 1000);
    } catch (error) {
      setToast({ message: 'Failed to retry workflow', type: 'error' });
    } finally {
      setLocalLoading(prev => ({ ...prev, retry: false }));
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setToast({ message: 'Copied to clipboard', type: 'success' });
  };

  const getRelativeTime = (timestamp) => {
    const now = Date.now();
    const diff = now - timestamp;
    const seconds = Math.floor(diff / 1000);
    
    if (seconds < 60) return `${seconds}s ago`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
  };

  const canTrigger = (request) => {
    return ['NEW', 'FAILED', 'CANCELLED'].includes(request?.status);
  };

  const canCancel = (request) => {
    return ['PENDING', 'IN_PROGRESS'].includes(request?.status);
  };

  const canRetry = (request) => {
    return request?.status === 'FAILED';
  };

  const canDelete = (request) => {
    return !['IN_PROGRESS'].includes(request?.status);
  };

  const handleDelete = () => {
    setConfirmAction({ open: true, type: 'delete' });
  };

  const executeDelete = async () => {
    setLocalLoading(prev => ({ ...prev, delete: true }));
    try {
      await deleteWorkflowRequest(id);
      navigate('/admin/workflows');
    } finally {
      setLocalLoading(prev => ({ ...prev, delete: false }));
      setConfirmAction({ open: false, type: null });
    }
  };

  const handleSave = async () => {
    setLocalLoading(prev => ({ ...prev, save: true }));
    try {
      const updates = {};
      if (editForm.status !== selectedWorkflowRequest.status) {
        updates.status = editForm.status;
      }
      if (editForm.lastError !== (selectedWorkflowRequest.lastError || '')) {
        updates.lastError = editForm.lastError || null;
      }

      if (Object.keys(updates).length > 0) {
        await updateWorkflowRequest(id, updates);
        setToast({ message: 'Changes saved', type: 'success' });
      }
      setIsEditing(false);
    } catch (error) {
      setToast({ message: 'Failed to save changes', type: 'error' });
    } finally {
      setLocalLoading(prev => ({ ...prev, save: false }));
    }
  };

  const handleCancelEdit = () => {
    setEditForm({
      status: selectedWorkflowRequest?.status || '',
      lastError: selectedWorkflowRequest?.lastError || ''
    });
    setIsEditing(false);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'NEW':
        return <Clock className="w-5 h-5 text-gray-500" />;
      case 'PENDING':
        return <AlertCircle className="w-5 h-5 text-yellow-500" />;
      case 'IN_PROGRESS':
        return <RefreshCw className="w-5 h-5 text-blue-500 animate-spin" />;
      case 'COMPLETED':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'FAILED':
        return <XCircle className="w-5 h-5 text-red-500" />;
      case 'CANCELLED':
        return <StopCircle className="w-4 h-4 text-gray-500" />;
      default:
        return <Clock className="w-5 h-5 text-gray-500" />;
    }
  };

  const getStatusBadge = (status) => {
    const baseClasses = 'inline-flex items-center px-3 py-1 rounded-full text-sm font-medium';
    
    switch (status) {
      case 'NEW':
        return `${baseClasses} bg-gray-100 text-gray-800`;
      case 'PENDING':
        return `${baseClasses} bg-yellow-100 text-yellow-800`;
      case 'IN_PROGRESS':
        return `${baseClasses} bg-blue-100 text-blue-800`;
      case 'COMPLETED':
        return `${baseClasses} bg-green-100 text-green-800`;
      case 'FAILED':
        return `${baseClasses} bg-red-100 text-red-800`;
      default:
        return `${baseClasses} bg-gray-100 text-gray-800`;
    }
  };

  const formatDate = (dateString) => {
    try {
      return new Date(dateString).toLocaleString();
    } catch {
      return 'Invalid Date';
    }
  };

  // ✅ NEW: Helper function to get business context from store data only
  const getBusinessContext = (request) => {
    const payload = request?.payload || {};
    
    // Extract Need information from store
    let needTitle = 'Unknown Need';
    let clientName = 'Unknown Client';
    let need = null;
    
    if (payload.needId) {
      // Validate need ID
      if (typeof payload.needId === 'string' && payload.needId.trim() !== '' && !payload.needId.includes('…')) {
        need = needs.find(n => n.id === payload.needId);
        if (need) {
          needTitle = need.title || 'Untitled Need';
          clientName = need.client || 'Unknown Client';
        } else {
          // Need not found in store - show dead data
          needTitle = payload.needTitle || payload.title || `Need ${payload.needId.substring(0, 8)}... (Not Found)`;
          clientName = payload.clientName || payload.client || payload.company || 'Unknown Client';
        }
      } else {
        // Invalid need ID
        needTitle = 'Invalid Need ID';
        clientName = 'Unknown Client';
      }
    }
    
    // Extract Candidate information from store
    let candidateNames = [];
    let candidates = [];
    
    if (payload.needId && typeof payload.needId === 'string' && payload.needId.trim() !== '' && !payload.needId.includes('…')) {
      try {
        candidates = getCandidatesForNeed(payload.needId);
        
        if (payload.candidateId) {
          // Single candidate from payload
          const specificCandidate = candidates.find(c => c.id === payload.candidateId);
          if (specificCandidate && specificCandidate.name) {
            candidateNames = [specificCandidate.name];
          } else {
            // Candidate not found in store - show dead data
            const candidateName = payload.candidateName || payload.candidate || 
                                 `Candidate ${payload.candidateId.substring(0, 8)}... (Not Found)`;
            candidateNames = [candidateName];
          }
        } else {
          // All candidates for the need
          if (candidates && candidates.length > 0) {
            candidateNames = candidates.map(c => {
              if (c.name) return c.name;
              return c.id ? `Candidate ${c.id.substring(0, 8)}...` : 'Unknown Candidate';
            });
          } else {
            // No candidates found in store - show dead data
            candidateNames = ['No candidates found'];
          }
        }
      } catch (error) {
        console.warn(`Error getting candidates for need ${payload.needId}:`, error);
        candidateNames = ['Error loading candidates'];
      }
    }
    
    const candidateDisplay = candidateNames.length > 0 ? candidateNames.join(', ') : 'No candidates';
    
    // Extract Step label from payload
    const stepLabel = payload.stepType ? 
                     payload.stepType.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase()) :
                     request?.type || 'Unknown Step';
    
    return { 
      needTitle, 
      clientName, 
      candidateDisplay, 
      stepLabel, 
      need, 
      candidates 
    };
  };

  // ✅ NEW: Load candidates for the need when workflow request is loaded
  useEffect(() => {
    if (selectedWorkflowRequest?.payload?.needId) {
      const needId = selectedWorkflowRequest.payload.needId;
      
      // Validate need ID
      if (typeof needId === 'string' && needId.trim() !== '' && !needId.includes('…')) {
        const candidates = getCandidatesForNeed(needId);
        if (candidates.length === 0) {
          // Load candidates if not in cache
          loadCandidatesForNeed(needId)
            .catch(error => {
              console.warn(`Error loading candidates for need ${needId}:`, error);
            });
        }
      }
    }
  }, [selectedWorkflowRequest, getCandidatesForNeed, loadCandidatesForNeed]);

  // ✅ NEW: Enhanced business context display
  const renderBusinessContext = () => {
    if (!selectedWorkflowRequest) return null;
    
    const context = getBusinessContext(selectedWorkflowRequest);
    
    return (
      <div className="bg-white shadow rounded-lg p-6 mb-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Business Context</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Need</label>
            <p className="mt-1 text-sm text-gray-900">
              {context.needTitle}
              {!context.need && (
                <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                  Not Found
                </span>
              )}
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Client</label>
            <p className="mt-1 text-sm text-gray-900">{context.clientName}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Candidates</label>
            <p className="mt-1 text-sm text-gray-900">
              {context.candidateDisplay}
              {context.candidates.length === 0 && (
                <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
                  No Data
                </span>
              )}
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Step</label>
            <p className="mt-1 text-sm text-gray-900">{context.stepLabel}</p>
          </div>
        </div>
      </div>
    );
  };

  if (workflowLoading && !selectedWorkflowRequest) {
    return (
      <div className="min-h-screen bg-gray-50">
        <AppHeader />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-center h-64">
            <RefreshCw className="h-8 w-8 text-gray-400 animate-spin" />
            <span className="ml-2 text-gray-500">Loading workflow request...</span>
          </div>
        </div>
      </div>
    );
  }

  if (workflowError) {
    return (
      <div className="min-h-screen bg-gray-50">
        <AppHeader />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="rounded-md bg-red-50 p-4">
            <div className="flex">
              <XCircle className="h-5 w-5 text-red-400" />
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Error loading workflow request</h3>
                <div className="mt-2 text-sm text-red-700">
                  <p>{workflowError}</p>
                </div>
                <div className="mt-4">
                  <Link
                    to="/admin/workflows"
                    className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-red-700 bg-red-100 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                  >
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    Back to List
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!selectedWorkflowRequest) {
    return (
      <div className="min-h-screen bg-gray-50">
        <AppHeader />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <h3 className="text-lg font-medium text-gray-900">Workflow request not found</h3>
            <div className="mt-4">
              <Link
                to="/admin/workflows"
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-blue-700 bg-blue-100 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to List
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <AppHeader />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <Link
            to="/admin/workflows"
            className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700"
          >
            <ArrowLeft className="mr-1 h-4 w-4" />
            Back to Workflow Requests
          </Link>
        </div>

        <div className="bg-white shadow overflow-hidden sm:rounded-lg">
          <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg leading-6 font-medium text-gray-900">
                  Workflow Request Details
                </h3>
                <p className="mt-1 max-w-2xl text-sm text-gray-500">
                  ID: {selectedWorkflowRequest.id}
                </p>
              </div>
              
              <div className="flex items-center space-x-3">
                {!isEditing ? (
                  <>
                    <button
                      onClick={() => setIsEditing(true)}
                      className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      <Edit3 className="mr-2 h-4 w-4" />
                      Edit
                    </button>
                    
                    <button
                      onClick={handleTrigger}
                      disabled={localLoading.trigger || selectedWorkflowRequest.status === 'IN_PROGRESS'}
                      className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {localLoading.trigger ? (
                        <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                      ) : (
                        <Play className="mr-2 h-4 w-4" />
                      )}
                      Trigger
                    </button>
                    
                    <button
                      onClick={handleDelete}
                      disabled={localLoading.delete}
                      className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {localLoading.delete ? (
                        <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                      ) : (
                        <Trash2 className="mr-2 h-4 w-4" />
                      )}
                      Delete
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      onClick={handleCancel}
                      className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      Cancel
                    </button>
                    
                    <button
                      onClick={handleSave}
                      disabled={localLoading.save}
                      className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {localLoading.save ? (
                        <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                      ) : (
                        <Save className="mr-2 h-4 w-4" />
                      )}
                      Save
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
          
          <div className="px-4 py-5 sm:p-6">
            <dl className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2">
              <div>
                <dt className="text-sm font-medium text-gray-500">Step ID</dt>
                <dd className="mt-1 text-sm text-gray-900 font-mono">
                  {selectedWorkflowRequest.stepId}
                </dd>
              </div>
              
              <div>
                <dt className="text-sm font-medium text-gray-500">Type</dt>
                <dd className="mt-1">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                    {selectedWorkflowRequest.type}
                  </span>
                </dd>
              </div>
              
              <div>
                <dt className="text-sm font-medium text-gray-500">Status</dt>
                <dd className="mt-1">
                  {isEditing ? (
                    <select
                      value={editForm.status}
                      onChange={(e) => setEditForm(prev => ({ ...prev, status: e.target.value }))}
                      className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                      aria-label="Status"
                    >
                      <option value="NEW">NEW</option>
                      <option value="PENDING">PENDING</option>
                      <option value="IN_PROGRESS">IN_PROGRESS</option>
                      <option value="COMPLETED">COMPLETED</option>
                      <option value="FAILED">FAILED</option>
                    </select>
                  ) : (
                    <div className="flex items-center">
                      {getStatusIcon(selectedWorkflowRequest.status)}
                      <span className={`ml-2 ${getStatusBadge(selectedWorkflowRequest.status)}`}>
                        {selectedWorkflowRequest.status}
                      </span>
                    </div>
                  )}
                </dd>
              </div>
              
              <div>
                <dt className="text-sm font-medium text-gray-500">Attempts</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {selectedWorkflowRequest.attempts}
                </dd>
              </div>
              
              <div>
                <dt className="text-sm font-medium text-gray-500">Created At</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {formatDate(selectedWorkflowRequest.createdAt)}
                </dd>
              </div>
              
              <div>
                <dt className="text-sm font-medium text-gray-500">Updated At</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {formatDate(selectedWorkflowRequest.updatedAt)}
                </dd>
              </div>
              
              <div className="sm:col-span-2">
                <dt className="text-sm font-medium text-gray-500">Last Error</dt>
                <dd className="mt-1">
                  {isEditing ? (
                    <textarea
                      value={editForm.lastError}
                      onChange={(e) => setEditForm(prev => ({ ...prev, lastError: e.target.value }))}
                      rows={3}
                      className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                      placeholder="No error message"
                      aria-label="Last error message"
                    />
                  ) : (
                    <div className="text-sm text-gray-900">
                      {selectedWorkflowRequest.lastError ? (
                        <div className="bg-red-50 border border-red-200 rounded-md p-3">
                          <pre className="whitespace-pre-wrap text-red-800 text-xs">
                            {selectedWorkflowRequest.lastError}
                          </pre>
                        </div>
                      ) : (
                        <span className="text-gray-500 italic">No error message</span>
                      )}
                    </div>
                  )}
                </dd>
              </div>
              
              <div className="sm:col-span-2">
                <dt className="text-sm font-medium text-gray-500">Payload</dt>
                <dd className="mt-1">
                  <div className="bg-gray-50 border border-gray-200 rounded-md p-4">
                    <pre className="text-xs text-gray-800 whitespace-pre-wrap">
                      {JSON.stringify(selectedWorkflowRequest.payload, null, 2)}
                    </pre>
                  </div>
                </dd>
              </div>
            </dl>
          </div>
        </div>
        {renderBusinessContext()}
      </div>

      <ConfirmDialog
        open={confirmAction.open}
        title={confirmAction.type === 'delete' ? 'Delete Workflow Request' : 'Cancel Workflow Request'}
        message={confirmAction.type === 'delete'
          ? 'Are you sure you want to delete this workflow request? This action cannot be undone.'
          : 'Are you sure you want to cancel this workflow request?'}
        confirmLabel={confirmAction.type === 'delete' ? 'Delete' : 'Cancel Request'}
        variant="danger"
        onConfirm={() => {
          if (confirmAction.type === 'delete') executeDelete();
          else executeCancel();
        }}
        onCancel={() => setConfirmAction({ open: false, type: null })}
      />

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
};

export default WorkflowRequestDetail;
